import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CaretLeft, CaretDown, CheckCircle, PlayCircle, SealCheck, ClipboardText, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CmsAssetProtocol, CmsProtocolDocumentation } from "@/components/cms/ProtocolRenderer";

const AM_TONE = {
  offen: "border-border bg-muted text-muted-foreground",
  in_bearbeitung: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  erledigt: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
};

export default function WartungsauftragDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => { api.get(`/v1/maintenance-jobs/${id}`).then((r) => setData(r.data)); }, [id]);
  useEffect(() => { load(); }, [load]);

  const start = async (aid) => { await api.post(`/v1/maintenance-jobs/${id}/assets/${aid}/start`); toast.success("Wartung begonnen"); setExpanded(aid); load(); };
  const onProtocolDone = () => { setExpanded(null); load(); };
  const closeJob = async () => {
    try { const { data: r } = await api.post(`/v1/maintenance-jobs/${id}/close`); toast.success("Standort-Wartung abgeschlossen", { description: `Bericht ${r.report_id.slice(0, 8)}` }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="space-y-4"><Skeleton className="h-24 rounded-md" /><Skeleton className="h-48 rounded-md" /></div>;
  const p = data.progress;
  const pct = p.total ? Math.round(p.erledigt / p.total * 100) : 0;
  const job = data.job;

  return (
    <div>
      <button onClick={() => navigate("/wartungsauftraege")} className="mb-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><CaretLeft size={16} /> Aufträge</button>
      <PageHeader title={`Wartung · ${job.site_name}`} subtitle={`${job.customer_name ? `${job.customer_name} · ` : ""}${job.monteur_name || "—"} · Status ${job.status}`}>
        {p.erledigt === p.total && p.total > 0 && job.status !== "abgeschlossen" && <Button size="sm" className="gap-2" onClick={closeJob} data-testid="close-job-btn"><SealCheck size={16} weight="bold" /> Standort abschließen</Button>}
      </PageHeader>

      <Card className="mb-6 rounded-md border p-4" data-testid="job-progress">
        <div className="mb-2 flex items-center justify-between text-sm"><Label2>Fortschritt</Label2>
          <span className="font-semibold">{p.erledigt}/{p.total} erledigt · {p.in_bearbeitung} in Arbeit · {p.offen} offen</span></div>
        <div className="h-3 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
      </Card>

      <Card className="rounded-md border" data-testid="job-assets">
        <div className="divide-y divide-border">
          {data.assets.map((am) => (
            <div key={am.id}>
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <button onClick={() => setExpanded(expanded === am.asset_id ? null : am.asset_id)} data-testid={`am-toggle-${am.asset_id}`} className="flex min-w-0 items-center gap-2 text-left" disabled={am.status === "offen"}>
                  <CaretDown size={16} className={`transition-transform ${expanded === am.asset_id ? "rotate-0" : "-rotate-90"} ${am.status === "offen" ? "opacity-30" : ""}`} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{am.asset_name}{am.kundenbezeichnung ? ` · ${am.kundenbezeichnung}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{am.asset_type || "—"}{am.completed_at ? ` · abgeschlossen ${new Date(am.completed_at).toLocaleDateString("de-DE")}` : ""}</div>
                  </div>
                </button>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant="outline" className={`rounded-md text-xs ${AM_TONE[am.status]}`}>{am.status}</Badge>
                  {am.status === "offen" && <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => start(am.asset_id)} data-testid={`am-start-${am.asset_id}`}><PlayCircle size={14} /> Start</Button>}
                  {am.status === "in_bearbeitung" && expanded !== am.asset_id && <Button size="sm" className="h-7 gap-1" onClick={() => setExpanded(am.asset_id)} data-testid={`am-open-${am.asset_id}`}><ClipboardText size={14} /> Protokoll</Button>}
                  {am.status === "erledigt" && <>
                    <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setExpanded(expanded === am.asset_id ? null : am.asset_id)} data-testid={`am-doc-${am.asset_id}`}><FileText size={14} /> Dokumentation</Button>
                    <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                  </>}
                </div>
              </div>
              {expanded === am.asset_id && am.status === "in_bearbeitung" && (
                <CmsAssetProtocol jobId={id} assetId={am.asset_id} onDone={onProtocolDone} />
              )}
              {expanded === am.asset_id && am.status === "erledigt" && (
                <CmsProtocolDocumentation jobId={id} assetId={am.asset_id} />
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
